import { yupResolver } from '@hookform/resolvers/yup';
import { useSingleFactorSignIn } from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  InputError,
  TermsOfService,
  TextInput,
  useAuthStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Checkbox } from 'react-native-paper';
import { boolean, object, string, type InferType } from 'yup';

const schema = object().shape({
  email: string().email('Must be a valid email address').required('Required'),
  hasAcceptedTerms: boolean().when('$isSignUp', {
    is: true,
    then: (schema) =>
      schema
        .oneOf([true], 'You must accept our terms to continue')
        .required('You must accept our terms to continue'),
  }),
});

type SignInFormValues = InferType<typeof schema>;

type Props = {
  isSignUp?: boolean;
};

export function EmailLoginForm({ isSignUp }: Props) {
  const { authModal, updateAuthModal } = useAuthStore();
  const { email: storeEmail = '' } = authModal;
  const styles = useDynamicStyles(createStyles);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignInFormValues>({
    defaultValues: { email: storeEmail },
    resolver: yupResolver(schema),
    context: { isSignUp },
  });
  const { mutate: signIn, isPending } = useSingleFactorSignIn();

  const onSubmit = async ({ email, hasAcceptedTerms }: SignInFormValues) => {
    signIn(
      {
        email,
        terms_accepted: isSignUp ? hasAcceptedTerms : undefined,
      },
      {
        onSuccess: () => {
          // Transition to confirm step, mark code as sent, and track previous substage
          updateAuthModal({
            currentStep: 'confirm',
            previousStep: isSignUp ? 'signup' : 'login',
            codeSent: true,
          });
        },
      },
    );
  };

  return (
    <View style={styles.form}>
      <View>
        <Controller
          control={control}
          render={({ field: { onChange, ...field } }) => (
            <TextInput
              label="Email"
              autoCapitalize="none"
              autoComplete="email"
              inputMode="email"
              error={!!errors.email}
              {...field}
              onChange={(value) => {
                onChange(value);
                updateAuthModal({ email: value, codeSent: false });
              }}
            />
          )}
          name="email"
        />
        <InputError fieldError={errors.email} />
      </View>
      {isSignUp && (
        <>
          <View>
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <View style={styles.termsSection}>
                  <Checkbox.Android
                    status={value ? 'checked' : 'unchecked'}
                    onPress={() => {
                      onChange(!value);
                    }}
                  />
                  <TermsOfService />
                </View>
              )}
              name="hasAcceptedTerms"
            />
            <InputError fieldError={errors.hasAcceptedTerms} />
          </View>
        </>
      )}

      <Button
        variant={ButtonVariants.PURPLE}
        size="medium"
        onPress={handleSubmit(onSubmit)}
        disabled={isPending}
        loading={isPending}
        style={styles.button}
      >
        {isSignUp ? 'Sign Up' : 'Log In'}
      </Button>
    </View>
  );
}

const createStyles = () =>
  StyleSheet.create({
    form: {
      width: '100%',
      maxWidth: 350,
      gap: 16,
    },
    button: {
      alignSelf: 'center',
      marginTop: 8,
    },
    termsSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  });
